import { MjmlColumn, MjmlSection, MjmlText } from "@faire/mjml-react";
import { ReviewRequestDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/trackable-types";
import {
  Alignment,
  CartLayoutType,
  getButtonSize,
  Section,
  SharedProps,
} from "@redotech/redo-model/email-builder";
import { REDO_REVIEW_HASH } from "@redotech/redo-model/storefront/navigation";
import { ReviewPrioritizationOption } from "@redotech/redo-model/team";
import { TrackableLineItem } from "@redotech/redo-model/trackable";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { EmailLineItem } from "./email-line-item";
import { OrderTableHeader } from "./order-summary";

export type ReviewRequestState = Section.ReviewRequest & SharedProps;

export const EmailReviewRequest = memo(function EmailReviewRequest(
  props: ReviewRequestState,
) {
  if (!props.schemaFieldName) throw new Error("schemaFieldName is required");
  const reviewRequest = props.schemaInstance[
    props.schemaFieldName
  ] as ReviewRequestDataType["Review Request"];

  const buttonSize = getButtonSize(
    props.buttonSize,
    props.buttonWidth,
    props.buttonHeight,
  );

  const filteredLineItem = reduceBasedOnReviewPrioritizationOption(
    reviewRequest.relevantLineItems.filter((item) => !item.isRedo),
    reviewRequest.reviewPrioritization,
  );

  const padding = props.sectionPadding;

  return (
    <MjmlSection
      backgroundColor={props.sectionColor}
      paddingBottom={padding.bottom}
      paddingLeft={padding.left}
      paddingRight={padding.right}
      paddingTop={padding.top}
    >
      <MjmlColumn>
        <MjmlText color={props.textColor} fontFamily={props.fontFamily}>
          <OrderTableHeader title={props.sectionHeader} />
          <EmailLineItem
            alignment={Alignment.LEFT}
            format={props.format}
            imageCornerRadius={props.imageCornerRadius}
            imageSize={props.imageSize}
            isBuilder={props.isBuilder}
            item={filteredLineItem}
            showDummyImages={props.isBuilder}
            showImage
            showPrice
            showQuantity
            showTitle
            showVariant
            stackType={CartLayoutType.ROWS}
            team={props.team}
            textColor={props.textColor}
            utm={props.utm}
          >
            <a
              href={`${reviewRequest.storeUrl}/?productId=${filteredLineItem.id}&email=${reviewRequest.customerEmail}${REDO_REVIEW_HASH}`}
              style={{
                pointerEvents: props.isBuilder ? "none" : "auto",
                color: props.buttonTextColor ?? "#ffffff",
                backgroundColor: props.buttonColor,
                borderRadius: `${props.buttonCornerRadius ?? 8}px`,
                height: `${buttonSize.height}px`,
                width: `${buttonSize.width}px`,
                display: "block",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                lineHeight: `${buttonSize.height}px`,
                textAlign: "center",
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              Leave a review
            </a>
          </EmailLineItem>
        </MjmlText>
      </MjmlColumn>
    </MjmlSection>
  );
});

const reduceBasedOnReviewPrioritizationOption = (
  relevantLineItems: TrackableLineItem[],
  filter: ReviewPrioritizationOption,
): TrackableLineItem => {
  switch (filter) {
    case ReviewPrioritizationOption.Random:
      return relevantLineItems[
        Math.floor(Math.random() * relevantLineItems.length)
      ];
    case ReviewPrioritizationOption.HighestPrice:
      return relevantLineItems.reduce((prev, current) =>
        prev.price > current.price ? prev : current,
      );
    //REVIEWS_TODO: Implement the rest of the cases
    case ReviewPrioritizationOption.HighestReview:
      return relevantLineItems[
        Math.floor(Math.random() * relevantLineItems.length)
      ];
    case ReviewPrioritizationOption.LowestReview:
      return relevantLineItems[
        Math.floor(Math.random() * relevantLineItems.length)
      ];
    default:
      assertNever(filter);
  }
};
